package com.crowpay.views.dialogs

import com.crowpay.PaymentSetupRequest
import com.crowpay.PaymentTransferMethod
import com.crowpay.SharedUtils
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.notNullContractor
import com.crowpay.sdk.notNullSession
import com.crowpay.selectedContractor
import com.crowpay.utils.Action
import com.crowpay.utils.renderDollars
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.requiredField
import com.crowpay.views.components.space
import com.crowpay.views.screens.common.DialogWithDialog
import com.crowpay.views.screens.common.secondDialog
import com.crowpay.views.screens.contractor.ContractorPaymentInfo
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.OutlineSemantic
import com.crowpay.views.theming.appTheme
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.colored
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.subTitle
import com.crowpay.views.theming.textButton
import com.crowpay.views.theming.title
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.KeyboardHints
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.await
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.affirmative
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.bold
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.DismissBackground
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.dismissBackground
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.textInput
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

class CollectFundsDialog(
    // this dialog should be project agnostic
    // projects have positive amount project ledger items that represent amounts in the escrow account
    val amount: Long = 0
) : DialogWithDialog, ContractorPaymentInfo() {

    val partial = Property<Long>(0)
    val transferMethod = Property(PaymentTransferMethod.TwoThreeDay)

    class AddBankAccountDialog(
        bankName: String = "",
        accountName: String = "",
        routingNumber: String = "",
        accountNumber: String = "",
        val retrievePaymentInfo: suspend ViewWriter.() -> Unit
    ) : Page, Validator() {
        val bankName = Property(bankName)
        val accountName = Property(accountName)
        val routingNumber = Property(routingNumber)
        val accountNumber = Property(accountNumber)

        override fun ViewWriter.render2(): ViewModifiable = dismissBackground {
            centered - dialog - stack {
                spacing = AppDimensions.fullIndent
                sizeConstraints(width = 40.rem) - col {
                    launch { retrievePaymentInfo() }

                    title("Add Bank Account")

                    space()

                    requiredField("Bank Name") {
                        val value = bankName.validate { it.isNotBlank() }
                        textInput {
                            hint = "Bank Name"
                            keyboardHints = KeyboardHints.title
                            content bind value
                        }
                    }

                    requiredField("Account Name") {
                        val value = accountName.validate { it.isNotBlank() }
                        textInput {
                            hint = "Account Name"
                            keyboardHints = KeyboardHints.title
                            content bind value
                        }
                    }

                    requiredField("Routing Number") {
                        val value =
                            routingNumber.validate { SharedUtils.isRoutingNumberValid(it) }
                        textInput {
                            hint = "Routing Number"
                            keyboardHints = KeyboardHints.integer
                            content bind value
                        }
                    }

                    requiredField("Account Number") {
                        val value = accountNumber.validate { it.isNotBlank() }
                        textInput {
                            hint = "Account Number"
                            content bind value
                        }
                    }

                    space()

                    atEnd - row {
                        textButton - button {
                            specCenteredText("Cancel")
                            action = Action("Cancel Add/Edit Bank Account") {
                                secondDialog.value = null
                            }
                        }

                        primaryButton - button {
                            ::enabled{ allValid() }
                            specCenteredText("Save Bank Account")
                            action = Action("Save Bank Account") {
                                val c = notNullContractor.await()

                                notNullSession().nonCached.contractor.setupPaymentInfo(
                                    id = c,
                                    input = PaymentSetupRequest(
                                        bankName = bankName.value,
                                        routingNumber = routingNumber.value.toInt(),
                                        accountNumber = accountNumber.value,
                                        accountName = accountName.value
                                    )
                                )
                                delay(500)
                                retrievePaymentInfo()

                                secondDialog.value = null
                            }
                        }
                    }
                }
            }
        }
    }

    override fun DismissBackground.renderDialog(): ViewModifiable =
        dialog - centered - stack {
            launch { retrievePaymentInfo() }

            spacing = AppDimensions.fullIndent
            col {
                themeChoice = ThemeDerivation(appTheme)
                spacing = 0.px
                row {
                    spacing = 0.px
                    space(AppDimensions.sectionIndent)
                    col {
                        title("Collect")
                        row {
                            spacing = 0.px
                            subTitle("Total available funds: ")
                            colored(AppColors.secondary.main) - bold - boldSubTitle {
                                ::content { amount.renderDollars() }
                            }
                        }
                    }
                    space(AppDimensions.sectionIndent)
                }

                space(AppDimensions.sectionIndent)

                greySeparator()

                space(AppDimensions.sectionIndent)

                row {
                    spacing = 0.px
                    space(AppDimensions.sectionIndent)
                    col {
                        boldSubTitle("Tell Us How To Send Your Money")

                        col {
                            spacing = AppDimensions.expandButtonSpace
                            body {
                                ::content { paymentInfo()?.bankName ?: "No Account Connected" }
                            }
                            body {
                                ::content { paymentInfo()?.accountName ?: "" }
                            }
                            gravity(Align.Start, Align.Center) - textButton - button {
                                spacing = 0.px
                                smallBody {
                                    ::content {
                                        if (paymentInfo() == null) "Add Bank Account" else "Edit Bank Account"
                                    }
                                }
                                action = Action("Add/Edit Bank Account") {
                                    val info = paymentInfo.state.getOrNull()

                                    secondDialog.value = AddBankAccountDialog(
                                        info?.bankName ?: "",
                                        info?.accountName ?: "",
                                    ) { retrievePaymentInfo() }
                                }
                            }
                        }

                        // payment options
                        // option 1: same day transfer
                        lightSection - buttonTheme - button {
                            enabled = false
//                                ::themeChoice {
//                                    this@button.themeChoice +
//                                            if (transferMethod() == PaymentTransferMethod.SameDay) {
//                                                OutlineSemantic(AppColors.secondary.main, 2.px)
//                                            } else {
//                                                OutlineSemantic(AppColors.secondary.main, 0.px)
//                                            }
//                                }
                            row {
                                body("icon")
                                col {
                                    spacing = AppDimensions.buttonSpacing
                                    boldSubTitle("Same Day")
                                    subTitle("(Not yet available)")
                                }
                            }

                            action = Action("Select Same Day Transfer Option") {
                                transferMethod set PaymentTransferMethod.SameDay
                            }
                        }

                        // option 2: 2-3 days
                        lightSection - buttonTheme - button {
                            ::enabled { paymentInfo() != null }
                            themeChoice += OutlineSemantic(AppColors.secondary.main, 2.px)
//                                ::themeChoice {
//                                    this@button.themeChoice +
//                                            if (transferMethod() == PaymentTransferMethod.TwoThreeDay) {
//                                                OutlineSemantic(AppColors.secondary.main, 2.px)
//                                            } else {
//                                                OutlineSemantic(AppColors.secondary.main, 0.px)
//                                            }
//                                }
                            row {
                                body("icon")
                                col {
                                    spacing = AppDimensions.buttonSpacing
                                    boldSubTitle("2-3 Days")
                                    subTitle("No Fee")
                                }
                            }

                            action = Action("Select 2-3 Day Transfer Option") {
                                transferMethod set PaymentTransferMethod.TwoThreeDay
                            }
                        }

                        // option 3: 2-3 days, I don't know if this is still planned or not
//                            lightSection - buttonTheme - button {
//                                ::enabled { paymentInfo() != null }
//                                ::themeChoice {
//                                    this@button.themeChoice +
//                                            if (transferMethod() == PaymentTransferMethod.CrowPayCard) {
//                                                OutlineSemantic(AppColors.secondary.main, 2.px)
//                                            } else {
//                                                OutlineSemantic(AppColors.secondary.main, 0.px)
//                                            }
//                                }
//                                row {
//                                    body("icon")
//                                    col {
//                                        spacing = AppDimensions.buttonSpacing
//                                        boldSubTitle("CrowPay Card")
//                                        subTitle("(Not yet available)")
//                                    }
//                                }
//
//                                action = Action("Select CrowPay Card Transfer Option") {
//                                    transferMethod set PaymentTransferMethod.CrowPayCard
//                                }
//                            }
                    }

                    space(AppDimensions.sectionIndent)
                }

                space(AppDimensions.sectionIndent)

                buttonTheme - affirmative - button {
                    ::enabled { paymentInfo() != null }
                    centered - bodyBold("Transfer")
                    action = Action("Transfer money out of CrowPay") {
                        val id = selectedContractor()
                        if (id != null) notNullSession().nonCached.contractor.requestCashOut(id)
                        retrievePaymentPendingCash()
                        dialogScreenNavigator.dismiss()
                    }
                }
            }
        }
}